import moment from 'moment';
import { formatDate } from '../../shared/services/dateService';
import { OBSERVATION_TYPE } from '../constants/observationType';

const AppraiserDataMapper = {
  appraiserDataToApolloInput: (appraiserData, domainExperiences) => ({
    CurrentRole: appraiserData.currentRole,
    AtlAffiliation: appraiserData.atlAffiliation ? formatDate(appraiserData.atlAffiliation) : null,
    AtmAffiliation: appraiserData.atmAffiliation ? formatDate(appraiserData.atmAffiliation) : null,
    AssignedAppraisals: appraiserData.assignedAppraisals
      ? appraiserData.assignedAppraisals.map(x => x.id)
      : null,
    DirectChannel: appraiserData.directChannel,
    Observation: appraiserData.observation,
    ...commonAppraiserDataMapping(appraiserData, domainExperiences)
  }),
  currentAppraiserDataToApolloInput: (appraiserData, domainExperiences) => ({
    ...commonAppraiserDataMapping(appraiserData, domainExperiences)
  }),
  appraiserDataToViewFields: appraiserData => ({
    email: appraiserData.UserLicense.Email,
    name: appraiserData.UserLicense.Name,
    systemRole: {
      id: appraiserData.UserLicense.SystemRole.Id
    },
    atlTotalAppraisals: appraiserData.ParticipationSummary.TotalAppraisals.Atl.CurrentAmount,
    atmTotalAppraisals: appraiserData.ParticipationSummary.TotalAppraisals.Atm.CurrentAmount,
    atlTotalEvents: appraiserData.ParticipationSummary.TotalEvents.Atl,
    atmTotalEvents: appraiserData.ParticipationSummary.TotalEvents.Atm,
    totalEvents: appraiserData.ParticipationSummary.TotalEvents.Total,
    atlObserverTotalEvents:
      appraiserData.ParticipationSummary.ObserverTotalEvents.Atl.CurrentAmount,
    atmObserverTotalEvents:
      appraiserData.ParticipationSummary.ObserverTotalEvents.Atm.CurrentAmount,
    company: appraiserData.Company,
    autoAssignedAppraisals: appraiserData.AssignedAppraisals
      ? appraisalMapping(appraiserData.AssignedAppraisals.filter(x => x.IsCurrentAtlAutoAssigned))
      : null,
    isObserver: appraiserData.Observation !== OBSERVATION_TYPE.NA
  }),
  appraiserDataToEditFields: appraiserData => ({
    desiredRole: appraiserData.DesiredRole,
    affiliationType: appraiserData.CurrentRole.AffiliationType,
    currentRole: appraiserData.CurrentRole.Value,
    atlAffiliation: appraiserData.AtlAffiliation
      ? moment(appraiserData.AtlAffiliation).toDate()
      : null,
    atmAffiliation: appraiserData.AtmAffiliation
      ? moment(appraiserData.AtmAffiliation).toDate()
      : null,
    city: appraiserData.City,
    state: appraiserData.State,
    country: appraiserData.Country,
    additionalInformation: appraiserData.AdditionalInformation ?? '',
    timeZoneId: appraiserData.TimeZoneId,
    willingToTravelToAfrica: appraiserData.WillingToTravelToAfrica,
    willingToTravelToAsia: appraiserData.WillingToTravelToAsia,
    willingToTravelToAustralia: appraiserData.WillingToTravelToAustralia,
    willingToTravelToEurope: appraiserData.WillingToTravelToEurope,
    willingToTravelToNorthAmerica: appraiserData.WillingToTravelToNorthAmerica,
    willingToTravelToSouthAmerica: appraiserData.WillingToTravelToSouthAmerica,
    willingToTravelToVirtual: appraiserData.WillingToTravelToVirtual,
    domainExperiences: appraiserData.DomainExperiences.map(x => ({
      domain: x.Domain,
      role: x.Role,
      hasDomainAccess: x.HasDomainAccess,
      yearsOfExperience: x.YearsOfExperience
    })),
    assignedAppraisals: appraiserData.AssignedAppraisals
      ? appraisalMapping(appraiserData.AssignedAppraisals.filter(x => !x.IsCurrentAtlAutoAssigned))
      : null,
    directChannel: appraiserData.DirectChannel,
    observation: appraiserData.Observation
  }),
  dataToDropDownList: data =>
    data.map(item => ({
      title: item.Title,
      value: item.Value
    })),
  currentRoleToDropDownList: data =>
    data.map(item => ({
      title: item.Title,
      value: item.Value,
      affiliationType: item.AffiliationType
    })),
  searchedAppraisalsToView: appraisals => appraisalMapping(appraisals)
};

const commonAppraiserDataMapping = (appraiserData, domainExperiences) => ({
  DesiredRole: appraiserData.desiredRole,
  City: appraiserData.city,
  State: appraiserData.state,
  Country: appraiserData.country,
  AdditionalInformation: mapAdditionalInformation(appraiserData.additionalInformation),
  TimeZoneId: appraiserData.timeZoneId,
  WillingToTravelToAfrica: appraiserData.willingToTravelToAfrica,
  WillingToTravelToAsia: appraiserData.willingToTravelToAsia,
  WillingToTravelToAustralia: appraiserData.willingToTravelToAustralia,
  WillingToTravelToEurope: appraiserData.willingToTravelToEurope,
  WillingToTravelToNorthAmerica: appraiserData.willingToTravelToNorthAmerica,
  WillingToTravelToSouthAmerica: appraiserData.willingToTravelToSouthAmerica,
  WillingToTravelToVirtual: appraiserData.willingToTravelToVirtual,
  DomainExperiences: domainExperiences?.map(x => ({
    Domain: x.domain,
    Role: x.role,
    YearsOfExperience: x.yearsOfExperience
  }))
});

const mapAdditionalInformation = additionalInformation =>
  additionalInformation === undefined ? undefined : additionalInformation?.trim() || null;

const appraisalMapping = appraisals => {
  return appraisals.map(x => ({
    id: x.Id,
    name: x.Name,
    orgUnitNames: x.AppraisalOrgUnits.map(appraisalOrgUnit => appraisalOrgUnit.MddapOrgUnits.Name)
      .sort()
      .join(', ')
  }));
};

export default AppraiserDataMapper;
